import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { GpsMap, useGeolocation, reverseGeocode } from "@/components/GpsMap";
import { ScheduleCard } from "@/components/ScheduleCard";
import { DirectDeposit } from "@/components/DirectDeposit";
import { PayReport } from "@/components/PayReport";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { MapPin, Play, Stop, CurrencyDollar, Timer, DownloadSimple, Receipt, Eye, EyeSlash, Bank, CaretLeft, CaretRight } from "@phosphor-icons/react";

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const { coords, error: geoError } = useGeolocation();
  const [status, setStatus] = useState(null);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stubFreq, setStubFreq] = useState("weekly");
  const [periods, setPeriods] = useState([]);
  const [hideEarnings, setHideEarnings] = useState(() => localStorage.getItem("hide_earnings") === "1");
  const [connect, setConnect] = useState(null);
  const [ddBusy, setDdBusy] = useState(false);
  const [payReports, setPayReports] = useState([]);
  const [payIdx, setPayIdx] = useState(0);
  const [hidePay, setHidePay] = useState(() => localStorage.getItem("hide_pay") === "1");
  const touchX = useRef(null);
  const year = new Date().getFullYear();

  const toggleHide = () => {
    const v = !hideEarnings;
    setHideEarnings(v);
    localStorage.setItem("hide_earnings", v ? "1" : "0");
  };
  const money = (n) => (hideEarnings ? "••••••" : `$${(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`);

  const refresh = useCallback(async () => {
    try {
      const [s, e, p, pr] = await Promise.all([
        api.get("/clock/status"),
        api.get("/time-entries"),
        api.get("/payroll/summary"),
        api.get(`/payroll/periods?frequency=${stubFreq}&year=${year}`),
      ]);
      setStatus(s.data);
      setEntries(e.data);
      setSummary(p.data.rows[0]);
      setPeriods(pr.data.periods);
    } catch {
      toast.error("Could not load your data. Please refresh.");
    }
  }, [stubFreq, year]);

  useEffect(() => { refresh(); }, [refresh]);

  const loadConnect = useCallback(async () => {
    try { setConnect((await api.get("/connect/status")).data); } catch { /* silent */ }
  }, []);

  const loadPayReports = useCallback(async () => {
    try { setPayReports((await api.get("/pay-reports/recent?limit=3")).data.reports); setPayIdx(0); } catch { /* silent */ }
  }, []);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("connect") === "return") toast.success("Thanks! We're verifying your bank details — this can take a moment.");
    if (p.get("connect")) window.history.replaceState({}, "", "/");
    loadConnect();
    loadPayReports();
  }, [loadConnect, loadPayReports]);

  const startDirectDeposit = async () => {
    setDdBusy(true);
    try {
      const { data } = await api.post("/connect/onboarding-link");
      window.location.assign(data.url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start direct-deposit setup");
      setDdBusy(false);
    }
  };

  const downloadStub = async (start, end) => {
    try {
      const res = await api.get(`/paystub/pdf?start=${start}&end=${end}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `paystub-${start}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Pay stub downloaded");
    } catch {
      toast.error("Could not generate pay stub");
    }
  };

  const doClock = async (dir) => {
    setBusy(true);
    try {
      let payload = { latitude: null, longitude: null, address: "Location unavailable" };
      if (coords) {
        const address = await reverseGeocode(coords.latitude, coords.longitude);
        payload = { latitude: coords.latitude, longitude: coords.longitude, address };
      }
      await api.post(`/clock/${dir}`, payload);
      toast.success(dir === "in" ? "Clocked in — location recorded" : "Clocked out — hours logged");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/tax/1099/pdf?employee_id=${user.id}&year=${year}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `1099-NEC-${year}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("1099-NEC downloaded");
    } catch {
      toast.error("Could not generate PDF");
    }
  };

  const clockedIn = status?.clocked_in;

  return (
    <Layout title="Employee Console">
      {connect && !connect.payouts_enabled && (
        <div className="mb-6 flex items-center justify-between gap-4 border-2 border-amber-400/60 bg-amber-50 px-5 py-3" data-testid="dd-nudge-banner">
          <div className="flex items-center gap-2 text-sm text-amber-900">
            <Bank size={18} weight="fill" />
            <span>{connect.has_account ? "Finish setting up direct deposit so we can pay you to your bank." : "Set up direct deposit to get paid straight to your bank account."}</span>
          </div>
          <Button size="sm" className="rounded-none shrink-0" onClick={startDirectDeposit} disabled={ddBusy} data-testid="dd-nudge-button">
            {ddBusy ? "Opening…" : connect.has_account ? "Continue" : "Set up"}
          </Button>
        </div>
      )}
      {payReports.length > 0 && (
        <div className="mb-6" data-testid="recent-pay-section">
          <div className="flex items-center justify-between mb-3">
            <div className="label-caps">Recent Pay</div>
            <button
              onClick={() => { const v = !hidePay; setHidePay(v); localStorage.setItem("hide_pay", v ? "1" : "0"); }}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              data-testid="hide-pay-toggle"
            >
              {hidePay ? <Eye size={16} /> : <EyeSlash size={16} />} {hidePay ? "Show" : "Hide"}
            </button>
          </div>

          <div
            className="relative max-w-md"
            onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
            onTouchEnd={(e) => {
              if (touchX.current == null) return;
              const dx = e.changedTouches[0].clientX - touchX.current;
              if (dx < -40) setPayIdx((i) => Math.min(i + 1, payReports.length - 1));
              if (dx > 40) setPayIdx((i) => Math.max(i - 1, 0));
              touchX.current = null;
            }}
          >
            <PayReport report={payReports[payIdx]} onOpen={downloadStub} hidden={hidePay} />
          </div>

          {payReports.length > 1 && (
            <div className="flex items-center justify-center gap-4 mt-3">
              <button
                onClick={() => setPayIdx((i) => Math.max(i - 1, 0))}
                disabled={payIdx === 0}
                className="p-1.5 disabled:opacity-30 hover:bg-muted transition-colors"
                data-testid="pay-prev"
              ><CaretLeft size={20} /></button>
              <div className="flex items-center gap-1.5">
                {payReports.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPayIdx(i)}
                    className={`h-2 rounded-full transition-all ${i === payIdx ? "w-5 bg-primary" : "w-2 bg-muted-foreground/30"}`}
                    data-testid={`pay-dot-${i}`}
                    aria-label={`Pay period ${i + 1}`}
                  />
                ))}
              </div>
              <button
                onClick={() => setPayIdx((i) => Math.min(i + 1, payReports.length - 1))}
                disabled={payIdx === payReports.length - 1}
                className="p-1.5 disabled:opacity-30 hover:bg-muted transition-colors"
                data-testid="pay-next"
              ><CaretRight size={20} /></button>
            </div>
          )}
        </div>
      )}
      <ScheduleCard />
      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        {/* Clock panel */}
        <Card className="rounded-2xl border-2 border-border lg:col-span-2 p-0 overflow-hidden shadow-[6px_6px_0_0_rgba(0,0,0,0.08)]">
          <div className="p-6 border-b border-border flex items-center justify-between">
            <div>
              <div className="label-caps">Time Clock</div>
              <h2 className="font-display font-bold text-2xl tracking-tighter mt-1">
                {clockedIn ? "You are ON the clock" : "You are OFF the clock"}
              </h2>
              {clockedIn && (
                <div className="text-sm text-muted-foreground font-mono mt-1" data-testid="active-since">
                  Since {fmt(status.active_entry.clock_in)}
                </div>
              )}
            </div>
            <span
              className={`w-3 h-3 rounded-full ${clockedIn ? "bg-primary animate-pulse" : "bg-muted-foreground/40"}`}
              data-testid="clock-status-dot"
            />
          </div>

          <GpsMap lat={coords?.latitude} lng={coords?.longitude} height={260} />

          <div className="p-6">
            <div className="flex items-center gap-2 text-sm font-mono mb-4" data-testid="gps-readout">
              <MapPin size={16} className="text-primary" weight="fill" />
              {coords
                ? `${coords.latitude.toFixed(5)}, ${coords.longitude.toFixed(5)}`
                : geoError
                ? `GPS error: ${geoError}`
                : "Acquiring GPS signal…"}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Button
                className="rounded-none h-14 text-base"
                disabled={busy || clockedIn}
                onClick={() => doClock("in")}
                data-testid="clock-in-button"
              >
                <Play size={20} weight="fill" className="mr-2" /> Clock In
              </Button>
              <Button
                variant="destructive"
                className="rounded-none h-14 text-base"
                disabled={busy || !clockedIn}
                onClick={() => doClock("out")}
                data-testid="clock-out-button"
              >
                <Stop size={20} weight="fill" className="mr-2" /> Clock Out
              </Button>
            </div>
          </div>
        </Card>

        {/* Earnings */}
        <div className="space-y-6">
          <Card className="rounded-2xl border-2 border-border p-6 shadow-[6px_6px_0_0_rgba(0,0,0,0.08)]">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 label-caps"><CurrencyDollar size={14} /> {year} Earnings</div>
              <button onClick={toggleHide} className="text-muted-foreground hover:text-foreground transition-colors" title={hideEarnings ? "Show earnings" : "Hide earnings"} data-testid="toggle-earnings">
                {hideEarnings ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="font-mono tabular text-4xl font-bold" data-testid="earnings-total">
              {money(summary?.total_pay)}
            </div>
            <div className="mt-4 pt-4 border-t border-border space-y-2 text-sm font-mono">
              <div className="flex justify-between"><span className="text-muted-foreground">Rate</span><span data-testid="hourly-rate">{hideEarnings ? "••••" : `$${(summary?.hourly_rate ?? 0).toFixed(2)}/hr`}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Total hours</span><span data-testid="total-hours">{(summary?.total_hours ?? 0).toFixed(2)}</span></div>
            </div>
          </Card>

          <Card className="rounded-2xl border-2 border-border p-6 shadow-[6px_6px_0_0_rgba(0,0,0,0.08)]" data-testid="tax-form-card">
            <div className="label-caps mb-2">Tax Form</div>
            <p className="text-sm text-muted-foreground mb-4">Download your {year} Form 1099-NEC nonemployee compensation statement.</p>
            <Button variant="outline" className="rounded-none w-full" onClick={downloadPdf} data-testid="download-1099-button">
              <DownloadSimple size={16} className="mr-2" /> Download 1099-NEC
            </Button>
          </Card>

          <div data-testid="direct-deposit-card">
            <DirectDeposit status={connect} busy={ddBusy} onStart={startDirectDeposit} />
          </div>
        </div>
      </div>

      {/* Time entries */}
      <Card className="rounded-2xl border-2 border-border mt-6 p-0 overflow-hidden shadow-[6px_6px_0_0_rgba(0,0,0,0.08)]">
        <div className="p-6 border-b border-border flex items-center gap-2 label-caps"><Timer size={14} /> Time Entries</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-mono" data-testid="time-entries-table">
            <thead>
              <tr className="label-caps text-left border-b border-border bg-muted/40">
                <th className="px-6 py-3 font-semibold">Clock In</th>
                <th className="px-6 py-3 font-semibold">In Location</th>
                <th className="px-6 py-3 font-semibold">Clock Out</th>
                <th className="px-6 py-3 font-semibold">Out Location</th>
                <th className="px-6 py-3 font-semibold text-right">Hours</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 && (
                <tr><td colSpan={5} className="px-6 py-10 text-center text-muted-foreground">No time entries yet.</td></tr>
              )}
              {entries.map((e, i) => (
                <tr key={e.id} className={i % 2 ? "bg-muted/20" : ""} data-testid={`entry-row-${i}`}>
                  <td className="px-6 py-3">{fmt(e.clock_in)}</td>
                  <td className="px-6 py-3 text-xs max-w-[220px] truncate" title={e.clock_in_address || ""}>{e.clock_in_address || `${e.clock_in_lat?.toFixed(4)}, ${e.clock_in_lng?.toFixed(4)}`}</td>
                  <td className="px-6 py-3">{fmt(e.clock_out)}</td>
                  <td className="px-6 py-3 text-xs max-w-[220px] truncate" title={e.clock_out_address || ""}>{e.clock_out_address || (e.clock_out_lat != null ? `${e.clock_out_lat.toFixed(4)}, ${e.clock_out_lng.toFixed(4)}` : "—")}</td>
                  <td className="px-6 py-3 text-right font-bold">{e.hours != null ? e.hours.toFixed(2) : "active"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pay stubs */}
      <Card className="rounded-none border-border mt-6 p-0" data-testid="paystubs-card">
        <div className="p-6 border-b border-border flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2 label-caps"><Receipt size={14} /> Pay Stubs</div>
          <div className="flex items-center border border-border" data-testid="stub-frequency-toggle">
            {["weekly", "biweekly"].map((f) => (
              <button
                key={f}
                onClick={() => setStubFreq(f)}
                className={`px-4 py-2 text-xs uppercase tracking-[0.15em] font-semibold transition-colors ${stubFreq === f ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                data-testid={`stub-freq-${f}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-mono" data-testid="paystubs-table">
            <thead>
              <tr className="label-caps text-left border-b border-border bg-muted/40">
                <th className="px-6 py-3 font-semibold">Pay Period</th>
                <th className="px-6 py-3 font-semibold text-right">Hours</th>
                <th className="px-6 py-3 font-semibold text-right">Gross Pay</th>
                <th className="px-6 py-3 font-semibold text-right">Stub</th>
              </tr>
            </thead>
            <tbody>
              {periods.length === 0 && (
                <tr><td colSpan={4} className="px-6 py-10 text-center text-muted-foreground">No pay periods with hours yet.</td></tr>
              )}
              {periods.map((p, i) => (
                <tr key={i} className={i % 2 ? "bg-muted/20" : ""} data-testid={`paystub-row-${i}`}>
                  <td className="px-6 py-3">{p.start} → {p.end}</td>
                  <td className="px-6 py-3 text-right">{p.total_hours.toFixed(2)}</td>
                  <td className="px-6 py-3 text-right font-bold">{money(p.total_pay)}</td>
                  <td className="px-6 py-3">
                    <div className="flex justify-end">
                      <Button variant="outline" size="sm" className="rounded-none" onClick={() => downloadStub(p.start, p.end)} data-testid={`download-stub-${i}`}>
                        <DownloadSimple size={14} className="mr-1.5" /> Pay Stub
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </Layout>
  );
}
